import React from 'react'
import { Link, useSearchParams } from 'react-router-dom';
import AppInfo from '../components/footer/AppInfo';
import { BrowserKey, BrowserLabel, detectBrowser } from '../utils/detectBrowser';
import { textToTitleCase } from '../utils/helpers';
import { APP_NAME } from '../constants/constants';

type Instructions = {
    browserSteps: string[];
    deviceSteps: string[];
};

const instructions: Record<BrowserKey, Instructions> = {
    "chrome-desktop": {
        browserSteps: [
            "Open Chrome's menu (three dots in the top-right corner).",
            "Go to 'Settings' > 'Privacy and security'.",
            "Click 'Site Settings'.",
            "Under 'Permissions', click 'Location'.",
            "Find this website in the list and set it to 'Allow'.",
        ],
        deviceSteps: [
            "Make sure location is enabled in your device settings (Windows: Settings > Privacy & Security > Location | macOS: System Settings > Privacy & Security > Location Services).",
            "Reload the page.",
        ],
    },
    "safari-mac": {
        browserSteps: [
            "In the top menu, click 'Safari'.",
            "Choose 'Settings' (or 'Preferences').",
            "Go to the 'Websites' tab.",
            "Select 'Location' from the sidebar.",
            "Find this website and set it to 'Allow'.",
        ],
        deviceSteps: [
            "Make sure location is enabled in your device settings (macOS: System Settings > Privacy & Security > Location Services).",
            "Reload the page.",
        ],
    },
    "chrome-android": {
        browserSteps: [
            "Open Chrome's menu (three dots in the top-right corner).",
            "Go to 'Settings'.",
            "Tap 'Site settings'.",
            "Tap 'Location'.",
            "Find this website in the list and set it to 'Allow'.",
        ],
        deviceSteps: [
            "Make sure location is enabled in your device settings (Android: Settings > Location).",
            "Reload the page.",
        ],
    },
    "chrome-ios": {
        browserSteps: [
            "Open Chrome's menu (three dots at the bottom or top-right).",
            "Tap 'Settings'.",
            "Tap 'Site settings'.",
            "Tap 'Location'.",
            "Find this website in the list and set it to 'Allow'.",
        ],
        deviceSteps: [
            "Make sure location is enabled in your device settings (iOS: Settings > Privacy & Security > Location Services > Chrome).",
            "Reload the page.",
        ],
    },
    "safari-ios": {
        browserSteps: [
            "Open Safari.",
            "Tap the 'page settings' icon (on the left side of the address bar).",
            "Tap 'Website Settings'.",
            "Tap 'Location'.",
            "Set it to 'Allow'.",
        ],
        deviceSteps: [
            "Make sure location is enabled in your device settings (iOS: Settings > Privacy & Security > Location Services > Safari Websites).",
            "Reload the page.",
        ],
    },
    "firefox": {
        browserSteps: [
            "Open Firefox's menu (three lines in the top-right corner).",
            "Click 'Settings'.",
            "Go to 'Privacy & Security'.",
            "Find the 'Permissions' section.",
            "Next to 'Location', click 'Settings'.",
            "Find this website and set it to 'Allow'.",
        ],
        deviceSteps: [
            "Make sure location is enabled in your device settings (Windows: Settings > Privacy & Security > Location | macOS: System Settings > Privacy & Security > Location Services).",
            "Reload the page.",
        ],
    },
    "unknown": {
        browserSteps: [
            "Open your browser settings.",
            "Find the 'Privacy' or 'Permissions' section.",
            "Select 'Location'.",
            "Find this website and set it to 'Allow'.",
        ],
        deviceSteps: [
            "Make sure location is enabled in your device settings (check system or phone settings for Location Services).",
            "Reload the page.",
        ],
    },
};

const BrowserPermissionHelp: React.FC = () => {
    const [searchParams] = useSearchParams();
    const browser = searchParams.get('browser') as BrowserKey;

    const currentBrowser = Object.keys(instructions).includes(browser)
        ? browser
        : detectBrowser();

    const { browserSteps, deviceSteps } = instructions[currentBrowser];

    return (
        <React.Fragment>
            <div className="card card-style p-4 text-center py-5">
                <p className="font-600 color-highlight mb-0 fs-6">Allow Location Access</p>
                <p className="font-600 color-highlight mb-0 fs-6">
                    {BrowserLabel[currentBrowser]}
                </p>

                {/* Browser Setting */}
                <div className="mt-3 text-start">
                    <p className="fw-bold">➤ Browser Setting - Give location access to {textToTitleCase(APP_NAME)} website</p>
                    <ul>
                        {browserSteps.map((step, index) => (
                            <li key={`browser-${index}`}>{step}</li>
                        ))}
                    </ul>
                </div>

                {/* Device Setting */}
                <div className="mt-3 text-start">
                    <p className="fw-bold">➤ Device Setting - Give location access to your browser</p>
                    <ul>
                        {deviceSteps.map((step, index) => (
                            <li key={`device-${index}`}>{step}</li>
                        ))}
                    </ul>
                </div>

                <Link
                    to="/"
                    data-back-button
                    className="btn btn-l btn-center-m font-700 gradient-highlight rounded-s mb-3"
                >
                    Back Home
                </Link>
            </div>
            <AppInfo />
        </React.Fragment>
    );
};

export default BrowserPermissionHelp;
